import { InstallPromptService } from './services/push/install-prompt.service';
import {
  importProvidersFrom,
  inject,
  LOCALE_ID,
  APP_INITIALIZER,
  provideAppInitializer,
} from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { ReactiveFormsModule } from '@angular/forms';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import {
  ErrorStateMatcher,
  MAT_DATE_LOCALE,
  MatRippleModule,
} from '@angular/material/core';
import { InputErrorStateMatcher } from './core/InputErrorStateMatcher';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatListModule } from '@angular/material/list';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import { AuthInterceptor } from './core/auth.interceptor';
import { EdulogTokenInterceptor } from './core/edulog-token.interceptor';
import { InflightGetDedupeInterceptor } from './core/inflight-get-dedupe.interceptor';
import { MatRadioModule } from '@angular/material/radio';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDateFnsModule } from '@angular/material-date-fns-adapter';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatStepperModule } from '@angular/material/stepper';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgxEditorModule } from 'ngx-editor';
import { ngxEditorConfiguration } from './config/ngx-editor.settings';
import { AuthConfigModule } from './auth/auth-config.module';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { TranslateModule } from '@ngx-translate/core';
import { SharedModule } from '../modules/shared/shared.module';
import { MatMenuModule } from '@angular/material/menu';
import { firebaseProviders } from './config/firebase.providers';
import { PermissionsService } from './services/permissions.service';
import { LocationsService } from './services/locations.service';
import { JobService } from './services/job.service';
import { SchoolService } from './services/school.service';
import { ProfileService } from './services/profile.service';
import { FileService } from './services/files.service';
import { ApplicationService } from './services/application.service';
import { BreadcrumbService } from './services/breadcrumb.service';
import { SettingService } from './services/setting.service';
import { NotificationService } from './services/notification.service';
import { SupportService } from './services/support.service';
import { PublicToolbarService } from './services/public-toolbar.service';
import { ApiVideoService } from './services/api-video.service';
import { NotificationSubscriptionService } from './services/notification-subscription.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { DatePipe } from '@angular/common';
import { DATE_LOCALES, LOCALE_IDS } from './i18n/locale';
import { DomSanitizer } from '@angular/platform-browser';
import { CUSTOM_ICONS, getIconName } from './config/custom-icons';
import { LanguageService, initialLanguage } from './services/language.service';
import { LevelLanguageSync } from './core/levels/level-i18n';

// Icon initialization provider factory
function provideCustomIconsInitializer() {
  return {
    provide: APP_INITIALIZER,
    useFactory: (iconRegistry: MatIconRegistry, domSanitizer: DomSanitizer) => {
      return () => {
        console.log('Registering custom icons...');
        iconRegistry.setDefaultFontSetClass('material-symbols-rounded');

        for (const icon of CUSTOM_ICONS) {
          const iconName = `custom_${getIconName(icon)}`;
          console.log(`Registering icon: ${iconName} -> ${icon}`);
          iconRegistry.addSvgIcon(
            iconName,
            domSanitizer.bypassSecurityTrustResourceUrl(icon)
          );
        }
        console.log('Custom icons registration complete');
      };
    },
    deps: [MatIconRegistry, DomSanitizer],
    multi: true,
  };
}

export const bootstrapProviders = [
  ...firebaseProviders,
  { provide: ErrorStateMatcher, useClass: InputErrorStateMatcher },
  // Fixed per page load: a language switch updates the texts right away and
  // dates and numbers on the next load (see LanguageService.apply).
  { provide: LOCALE_ID, useFactory: () => LOCALE_IDS[initialLanguage()] },
  PermissionsService,
  LocationsService,
  JobService,
  SchoolService,
  ProfileService,
  FileService,
  ApplicationService,
  BreadcrumbService,
  SettingService,
  NotificationService,
  SupportService,
  PublicToolbarService,
  ApiVideoService,
  NotificationSubscriptionService,
  DeviceDetectorService,
  {
    provide: MAT_DATE_LOCALE,
    useFactory: () => DATE_LOCALES[initialLanguage()],
  },
  provideCustomIconsInitializer(),
  provideAppInitializer(() => {
    const language = inject(LanguageService);
    language.init();
    language.start().subscribe();
  }),
  provideAppInitializer(() => inject(InstallPromptService).start()),
  // Level names in services (school and profile cards) follow the language.
  provideAppInitializer(() => void inject(LevelLanguageSync)),
  DatePipe,
  {
    provide: HTTP_INTERCEPTORS,
    useClass: InflightGetDedupeInterceptor,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: EdulogTokenInterceptor,
    multi: true,
  },
  { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
  provideHttpClient(withXhr(), withInterceptorsFromDi()),
  importProvidersFrom([
    BrowserAnimationsModule,
    AppRoutingModule,
    ReactiveFormsModule,
    MatToolbarModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatInputModule,
    MatAutocompleteModule,
    MatSelectModule,
    MatTabsModule,
    MatListModule,
    MatIconModule,
    MatDialogModule,
    MatSnackBarModule,
    MatRadioModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatDateFnsModule,
    MatSlideToggleModule,
    MatChipsModule,
    MatTooltipModule,
    MatSidenavModule,
    MatRippleModule,
    MatStepperModule,
    MatFormFieldModule,
    NgxEditorModule.forRoot(ngxEditorConfiguration),
    AuthConfigModule,
    DragDropModule,
    TranslateModule.forRoot(),
    SharedModule,
    MatMenuModule,
  ]),
];
